/**
 * SDF-based wall partition.
 *
 * Replaces the offset-line-intersection partition (`computeWallRegions`) with
 * one defined by bisectors of per-edge capsule SDFs. Conceptually:
 *
 *   - Every wall edge is a CAPSULE: a line segment from start to end, with a
 *     left half-thickness and a right half-thickness. The capsule's outline
 *     is the wall's offset boundary.
 *   - The wall footprint is the union of all capsules.
 *   - Inside the footprint, each point belongs to whichever capsule's
 *     centerline is closest — a Voronoi-style partition. The boundary
 *     between two adjacent walls' regions is the BISECTOR of their
 *     centerlines, NOT the intersection of their offset lines.
 *
 * Bisectors don't overshoot. The previous offset-line model produced cap
 * corners that could project axially past the next node, which crooked
 * adjacent walls. Bisectors are always between the two walls — they
 * cannot project past either endpoint. This eliminates the bowing /
 * crooked-wall bug class.
 *
 * The partition emits ONE polygon per wall edge (no separate node cells —
 * the junction's kernel is split among the adjacent walls along their
 * shared bisectors). Per-EDGE heights work; per-NODE heights are absorbed
 * by the adjacent walls' regions at the junction. (See `resolveBounds`.)
 *
 * Output matches the `WallRegion[]` shape the existing downstream code
 * already consumes (extrusion, 2D footprint, debug overlay).
 */

import * as THREE from 'three';
import type { SkeletonNodeData, SkeletonEdgeData } from '@pointorama/potree-shared';
import type { WallRegion } from './stitchedWallMesh';
import type { ResolveBoundsFn } from './computeWallRegions';
import { defaultResolveBounds } from './computeWallRegions';
import { arcFromBulge, edgeIsCurved, edgeLength, pointAtArcLength, tessellateBand, outgoingTangentAtStart, outgoingTangentAtEnd } from './arcGeometry';

interface Capsule {
  edge: SkeletonEdgeData;
  ax: number; // start.x
  ay: number;
  bx: number; // end.x
  by: number;
  dirX: number; // unit CHORD tangent (start → end)
  dirY: number;
  perpX: number; // unit left-perpendicular of the chord (= (-dirY, dirX))
  perpY: number;
  /** Arc curvature (0 = straight). Positive bows left of the chord. */
  bulge: number;
  /**
   * Unit tangents pointing OUT of each endpoint into the world (away from the
   * edge). For straight edges outStart = dir and outEnd = −dir; for arcs the
   * local circle tangents. Junction connectors + pass-through welds miter
   * against these so arcs joining straights behave like the local tangent line.
   */
  outStartX: number;
  outStartY: number;
  outEndX: number;
  outEndY: number;
  /** Arc length for curved edges, chord length for straight ones. */
  length: number;
  leftSize: number;
  rightSize: number;
}

function buildCapsule(edge: SkeletonEdgeData, startNode: SkeletonNodeData, endNode: SkeletonNodeData): Capsule | null {
  const ax = startNode.position.x;
  const ay = startNode.position.y;
  const bx = endNode.position.x;
  const by = endNode.position.y;
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return null;
  const dirX = dx / len;
  const dirY = dy / len;
  const perpX = -dirY;
  const perpY = dirX;
  const leftSize = edge.leftSize || 0.1;
  const rightSize = edge.rightSize || 0.1;
  const bulge = edgeIsCurved(edge) ? (edge.bulge as number) : 0;
  const a = { x: ax, y: ay };
  const b = { x: bx, y: by };
  // outStart points INTO the edge from the start node's perspective — but the
  // junction code wants "away from the node", which for the start node IS the
  // travel tangent, and for the end node the reversed travel tangent. Both are
  // exactly what outgoingTangentAtStart/End return.
  const ts = outgoingTangentAtStart(a, b, bulge);
  const te = outgoingTangentAtEnd(a, b, bulge);
  return {
    edge,
    ax,
    ay,
    bx,
    by,
    dirX,
    dirY,
    perpX,
    perpY,
    bulge,
    outStartX: ts.x,
    outStartY: ts.y,
    outEndX: te.x,
    outEndY: te.y,
    length: edgeLength(a, b, bulge),
    leftSize,
    rightSize,
  };
}

/**
 * Snap a point to a shared quantisation grid so two independent
 * computations of the same intersection produce bit-identical output.
 * 1e-4 (0.1 mm) is COARSER than the welder's 1e-5 (10 µm) precision, so
 * any tiny floating-point divergence (different `lineLineIntersection`
 * argument order, etc.) is erased BEFORE the welder hashes the point.
 * Two cap corners that mean the same geometric point land on the same
 * snapped value → identical `.toFixed(5)` key → welded into one vertex.
 */
function snapPoint(p: { x: number; y: number }): { x: number; y: number } {
  return { x: Math.round(p.x * 1e4) / 1e4, y: Math.round(p.y * 1e4) / 1e4 };
}

/**
 * Build the polygon ring for capsule `i`.
 *
 * Strategy: emit the wall as its NOMINAL RECTANGLE (perpendicular
 * thickness ± offsets, extending from start node to end node). No
 * mitering, no clipping.
 *
 * Polygons OVERLAP at junctions where adjacent walls' rectangles cover
 * the same inner-corner area. This is intentional: downstream
 * `Manifold.union` is robust to overlaps and produces a clean watertight
 * 3D mesh. Trying to make every wall polygon perfectly tile with its
 * neighbours via bisector clipping is fragile (any FP mismatch at a
 * shared vertex shows up as a visible crack/notch in the rendered mesh),
 * whereas overlapping rectangles unioned together are robustly correct.
 */
function buildEdgePolygon(capsules: Capsule[], i: number): THREE.Vector2[] | null {
  const c = capsules[i];
  if (c.bulge !== 0) {
    // Curved wall: tessellated arc band (left offset arc + reversed right
    // offset arc). Same ring ordering as the straight rectangle below, so
    // downstream winding assumptions are unchanged; Manifold's union absorbs
    // junction overlaps exactly as it does for rectangles.
    const ring = tessellateBand({ x: c.ax, y: c.ay }, { x: c.bx, y: c.by }, c.bulge, c.leftSize, c.rightSize).map(snapPoint);
    if (ring.length < 3) return null;
    return ring.map((p) => new THREE.Vector2(p.x, p.y));
  }
  const sl = snapPoint({ x: c.ax + c.perpX * c.leftSize, y: c.ay + c.perpY * c.leftSize });
  const el = snapPoint({ x: c.bx + c.perpX * c.leftSize, y: c.by + c.perpY * c.leftSize });
  const er = snapPoint({ x: c.bx - c.perpX * c.rightSize, y: c.by - c.perpY * c.rightSize });
  const sr = snapPoint({ x: c.ax - c.perpX * c.rightSize, y: c.ay - c.perpY * c.rightSize });
  return [new THREE.Vector2(sl.x, sl.y), new THREE.Vector2(el.x, el.y), new THREE.Vector2(er.x, er.y), new THREE.Vector2(sr.x, sr.y)];
}

/**
 * One node of a junction plus the walls that reach it from OUTSIDE the
 * junction. Ordinary junctions have a single member; junctions merged
 * across a short connector edge (see the clustering pass in
 * {@link computeSdfWallRegions}) have one member per collapsed node.
 */
interface JunctionMember {
  nodePos: { x: number; y: number };
  incidentIdxs: number[];
  /**
   * Capsules INTERNAL to a merged junction at this node (the short connector
   * edge whose endpoints collapsed into the cluster). Excluded from the
   * angular miter pairing — mitering the external walls against them would
   * re-introduce the chamfer the merge removes — but their band's lateral
   * extent still bounds the junction: their cap corners and face
   * intersections with the external walls feed the hull (without them, a
   * Z-jog between antiparallel walls degenerates to a zero-width sliver —
   * the external pair is skipped as pass-through and every remaining hull
   * point is collinear along the jog axis).
   */
  internalIdxs: number[];
}

/**
 * Build a JUNCTION-CONNECTOR polygon over a junction spanning one or more
 * nodes, filling the V-notch where adjacent walls' nominal rectangles
 * would otherwise leave a gap at the convex outer corner.
 *
 * The polygon is the CONVEX HULL of:
 *   - Every junction node position
 *   - Each incident wall's two cap corners (offset perpendicular from its
 *     own anchor node)
 *   - For each angular-neighbour PAIR of walls, the intersection of their
 *     offset BOUNDARIES (offset line for straight walls, offset circle for
 *     arcs — the proper miter point at the outer corner) — this is what
 *     extends the hull to fully cover the corner triangle.
 *
 * Manifold's union absorbs this with the wall rectangles to produce a
 * connected, fully-mitered wall mass.
 */
function buildJunctionConnector(capsules: Capsule[], members: JunctionMember[]): THREE.Vector2[] | null {
  const stubList = members.flatMap((member) => member.incidentIdxs.map((idx) => ({ idx, nodePos: member.nodePos })));
  if (stubList.length < 2) return null;
  // Outgoing-frame data per incident wall, sorted by angle for neighbour
  // pairing. Each wall is anchored at ITS OWN endpoint node (`nodePos`) —
  // merged junctions span several nodes, so the anchor travels with the wall.
  const buildStub = (idx: number, nodePos: { x: number; y: number }) => {
    const c = capsules[idx];
    const atStart = Math.abs(nodePos.x - c.ax) < 1e-9 && Math.abs(nodePos.y - c.ay) < 1e-9;
    // Outgoing TANGENT at the node (chord direction for straight edges).
    const outDirX = atStart ? c.outStartX : c.outEndX;
    const outDirY = atStart ? c.outStartY : c.outEndY;
    const perpX = -outDirY;
    const perpY = outDirX;
    const outLeftSize = atStart ? c.leftSize : c.rightSize;
    const outRightSize = atStart ? c.rightSize : c.leftSize;
    const leftCapX = nodePos.x + perpX * outLeftSize;
    const leftCapY = nodePos.y + perpY * outLeftSize;
    const rightCapX = nodePos.x - perpX * outRightSize;
    const rightCapY = nodePos.y - perpY * outRightSize;
    // Miter boundaries: straight walls miter against their offset LINE, arcs
    // against their offset CIRCLE. Mitering an arc against its tangent line
    // instead overshoots the band wherever it curves away from the tangent —
    // the apex lands OUTSIDE the wall material and the convex-hull connector
    // sticks out of the junction as a visible spike.
    const geom = c.bulge !== 0 ? arcFromBulge({ x: c.ax, y: c.ay }, { x: c.bx, y: c.by }, c.bulge) : null;
    let leftBoundary: MiterBoundary;
    let rightBoundary: MiterBoundary;
    if (geom) {
      // World-side offset radii (same formula + clamp as tessellateBandPair).
      // The outgoing frame at the END node swaps left/right sides.
      const s = Math.sign(c.bulge);
      const edgeLeftR = Math.max(1e-4, geom.radius + c.leftSize * s);
      const edgeRightR = Math.max(1e-4, geom.radius - c.rightSize * s);
      // An apex is only real material boundary within the arc's angular span.
      // Allow a modest overshoot past the end cap — a straight right-angle
      // miter extends one thickness past its cap, so 1.5× thickness keeps
      // ordinary corners mitered while near-tangential junctions (whose apex
      // would run far along the phantom circle) fall back to a chamfer.
      const slackS = 1.5 * Math.max(c.leftSize, c.rightSize);
      const circle = (r: number): MiterBoundary => ({
        kind: 'circle',
        cx: geom.center.x,
        cy: geom.center.y,
        r,
        startAngle: geom.startAngle,
        sweep: geom.sweep,
        slackAngle: slackS / Math.max(r, 1e-4),
      });
      leftBoundary = circle(atStart ? edgeLeftR : edgeRightR);
      rightBoundary = circle(atStart ? edgeRightR : edgeLeftR);
    } else {
      leftBoundary = { kind: 'line', px: leftCapX, py: leftCapY, dx: outDirX, dy: outDirY };
      rightBoundary = { kind: 'line', px: rightCapX, py: rightCapY, dx: outDirX, dy: outDirY };
    }
    return {
      anchorX: nodePos.x,
      anchorY: nodePos.y,
      outDirX,
      outDirY,
      outLen: c.length,
      angle: Math.atan2(outDirY, outDirX),
      leftCapX,
      leftCapY,
      rightCapX,
      rightCapY,
      leftBoundary,
      rightBoundary,
    };
  };
  const arr = stubList.map(({ idx, nodePos }) => buildStub(idx, nodePos));
  arr.sort((a, b) => a.angle - b.angle);
  const m = arr.length;

  const pts: { x: number; y: number }[] = members.map((member) => ({ x: member.nodePos.x, y: member.nodePos.y }));
  for (const w of arr) {
    pts.push({ x: w.leftCapX, y: w.leftCapY });
    pts.push({ x: w.rightCapX, y: w.rightCapY });
  }
  // For each adjacent angular pair, compute the offset-boundary miter
  // intersection — the apex of the notch between the two walls. Wall W's
  // LEFT boundary meets the CCW neighbour's RIGHT boundary. Across both
  // ordered pairs this yields BOTH the outer (convex) and inner (concave)
  // corner of the bend; both are needed to fill the full junction kernel,
  // so a degree-2 bend stays connected.
  //
  // We reject only a miter that flies FAR from the node — the previous
  // "cap at 5× thickness" let shallow-angle / short-wall (sliver)
  // junctions push the apex out into a visible spike. A legitimate miter
  // sits within ~2× the incident thickness; we cap at 3× as the spike
  // threshold AND reject any apex that projects past BOTH walls' far ends
  // (it would land beyond the wall material entirely). Note we do NOT
  // reject "behind the node": the inner corner of a convex bend is on the
  // concave side and must be kept, or the corner notch isn't filled.
  const APEX_EPS = 1e-6;
  const maxThick = Math.max(...arr.map((w) => Math.max(
    Math.hypot(w.leftCapX - w.anchorX, w.leftCapY - w.anchorY),
    Math.hypot(w.rightCapX - w.anchorX, w.rightCapY - w.anchorY),
  )));
  const SPIKE_CAP = 3 * maxThick;
  // Reference for picking the intersection candidate nearest the junction:
  // single-node junctions use the node itself, merged ones the member centroid.
  let refX = 0;
  let refY = 0;
  for (const member of members) {
    refX += member.nodePos.x;
    refY += member.nodePos.y;
  }
  refX /= members.length;
  refY /= members.length;
  const nearestMemberDist = (x: number, y: number): number => {
    let best = Infinity;
    for (const member of members) best = Math.min(best, Math.hypot(x - member.nodePos.x, y - member.nodePos.y));
    return best;
  };
  for (let i = 0; i < m; i++) {
    const self = arr[i];
    const ccw = arr[(i + 1) % m];
    // Near-antiparallel neighbours are a straight PASS-THROUGH, not a corner: the
    // two walls continue as one line (possibly stepping in thickness). Their
    // offset lines are near-parallel, so the "miter" intersection is a far-flung
    // spurious point — including it makes the convex hull chamfer the step. Skip
    // it; the step is filled correctly by the cap corners alone.
    const dirDot = self.outDirX * ccw.outDirX + self.outDirY * ccw.outDirY;
    if (dirDot < PASS_THROUGH_DOT) continue;
    const hit = miterBoundaryIntersect(self.leftBoundary, ccw.rightBoundary, { x: refX, y: refY });
    if (!hit) continue;
    // A miter far from every junction node is a spike, not a corner — drop it.
    if (nearestMemberDist(hit.x, hit.y) > SPIKE_CAP) continue;
    // Past BOTH walls' far ends → beyond the wall material (sliver overshoot).
    const tSelf = (hit.x - self.anchorX) * self.outDirX + (hit.y - self.anchorY) * self.outDirY;
    const tCcw = (hit.x - ccw.anchorX) * ccw.outDirX + (hit.y - ccw.anchorY) * ccw.outDirY;
    if (tSelf > self.outLen + APEX_EPS && tCcw > ccw.outLen + APEX_EPS) continue;
    pts.push(hit);
  }

  let hull = convexHull(pts.map(snapPoint));

  // DEGENERATE-HULL fallback: in a merged Z-jog between (near-)antiparallel
  // walls, the external pair is skipped as a pass-through, their offset lines
  // are parallel (no miter anyway), and every cap corner lies (near) the jog
  // axis — the hull collapses to a millimeters-wide sliver and the jog's
  // outer corners render unskinned. Detect it by the hull's MINIMUM WIDTH: a
  // real junction hull is at least about as wide as its thinnest incident
  // wall band. Only then, widen the hull with the INTERNAL connector walls'
  // lateral extent: their cap corners plus the intersections of their offset
  // faces with each member's external wall faces (the junction kernel
  // corners). This stays a fallback — in ordinary merged L/T junctions the
  // convex hull cannot also represent the ROOM's concave corner wedge, so
  // feeding the kernel corners unconditionally would fill room area. (The
  // width test may also fire on thin single-node hulls — legit pass-through
  // steps — but those have no internal walls, so the widening is a no-op.)
  const minStubThickness = Math.min(...arr.map((w) => Math.hypot(w.leftCapX - w.rightCapX, w.leftCapY - w.rightCapY)));
  if (hull.length < 3 || hullMinWidth(hull) < 0.5 * minStubThickness) {
    for (const member of members) {
      for (const internalIdx of member.internalIdxs) {
        const internal = buildStub(internalIdx, member.nodePos);
        pts.push({ x: internal.leftCapX, y: internal.leftCapY });
        pts.push({ x: internal.rightCapX, y: internal.rightCapY });
        for (const externalIdx of member.incidentIdxs) {
          const external = buildStub(externalIdx, member.nodePos);
          for (const internalBoundary of [internal.leftBoundary, internal.rightBoundary]) {
            for (const externalBoundary of [external.leftBoundary, external.rightBoundary]) {
              const hit = miterBoundaryIntersect(externalBoundary, internalBoundary, member.nodePos);
              if (!hit) continue;
              if (nearestMemberDist(hit.x, hit.y) > SPIKE_CAP) continue;
              pts.push(hit);
            }
          }
        }
      }
    }
    hull = convexHull(pts.map(snapPoint));
  }

  if (hull.length < 3) return null;
  return hull.map((p) => new THREE.Vector2(p.x, p.y));
}

/**
 * Minimum width of a convex hull: over every hull edge, the farthest vertex
 * distance from that edge's line — the smallest of those is the hull's
 * narrow dimension (rotating-calipers width for convex polygons). 0 for
 * degenerate hulls.
 */
function hullMinWidth(hull: { x: number; y: number }[]): number {
  if (hull.length < 3) return 0;
  let minWidth = Infinity;
  for (let i = 0; i < hull.length; i++) {
    const a = hull[i];
    const b = hull[(i + 1) % hull.length];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const len = Math.hypot(ex, ey);
    if (len < 1e-12) continue;
    let maxDist = 0;
    for (const p of hull) {
      const d = Math.abs((p.x - a.x) * ey - (p.y - a.y) * ex) / len;
      if (d > maxDist) maxDist = d;
    }
    minWidth = Math.min(minWidth, maxDist);
  }
  return Number.isFinite(minWidth) ? minWidth : 0;
}

/**
 * A wall's offset boundary at a junction: an infinite offset line for
 * straight walls (point + unit direction), the offset circle for arcs.
 * Circle boundaries carry the arc's angular span — only the part of the
 * circle actually covered by the band (± a slack past the caps) counts as
 * material boundary for mitering.
 */
type MiterBoundary =
  | { kind: 'line'; px: number; py: number; dx: number; dy: number }
  | { kind: 'circle'; cx: number; cy: number; r: number; startAngle: number; sweep: number; slackAngle: number };

const TAU = Math.PI * 2;

/**
 * True when (x, y) lies within the circle boundary's arc span, allowing
 * `slackAngle` overshoot past either end cap (travel < 0 wraps to near 2π).
 */
function circleSpanContains(c: MiterBoundary & { kind: 'circle' }, x: number, y: number): boolean {
  const a = Math.atan2(y - c.cy, x - c.cx);
  const travel = c.sweep >= 0 ? (((a - c.startAngle) % TAU) + TAU) % TAU : (((c.startAngle - a) % TAU) + TAU) % TAU;
  return travel <= Math.abs(c.sweep) + c.slackAngle || travel >= TAU - c.slackAngle;
}

/**
 * Miter apex of two offset boundaries — the intersection nearest the
 * junction node, keeping only hits that lie on actual material boundary
 * (within each circle boundary's arc span). Returns null when nothing
 * qualifies, in which case the cap corners alone bound the junction fill —
 * a chamfered corner beats a fabricated apex poking outside the wall.
 */
function miterBoundaryIntersect(a: MiterBoundary, b: MiterBoundary, near: { x: number; y: number }): { x: number; y: number } | null {
  if (a.kind === 'line' && b.kind === 'line') {
    return segmentLineIntersect(a.px, a.py, a.px + a.dx, a.py + a.dy, b.px, b.py, b.px + b.dx, b.py + b.dy);
  }
  let candidates: Array<{ x: number; y: number }>;
  if (a.kind === 'line' && b.kind === 'circle') candidates = lineCircleIntersections(a, b);
  else if (a.kind === 'circle' && b.kind === 'line') candidates = lineCircleIntersections(b, a);
  else if (a.kind === 'circle' && b.kind === 'circle') candidates = circleCircleIntersections(a, b);
  else return null;
  let best: { x: number; y: number } | null = null;
  let bestD = Infinity;
  for (const p of candidates) {
    if (a.kind === 'circle' && !circleSpanContains(a, p.x, p.y)) continue;
    if (b.kind === 'circle' && !circleSpanContains(b, p.x, p.y)) continue;
    const d = Math.hypot(p.x - near.x, p.y - near.y);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best ? snapPoint(best) : null;
}

function lineCircleIntersections(line: MiterBoundary & { kind: 'line' }, circle: MiterBoundary & { kind: 'circle' }): Array<{ x: number; y: number }> {
  // |p + t·d − c|² = r², d is unit → t² + bt + c0 = 0.
  const ox = line.px - circle.cx;
  const oy = line.py - circle.cy;
  const b = 2 * (ox * line.dx + oy * line.dy);
  const c0 = ox * ox + oy * oy - circle.r * circle.r;
  const disc = b * b - 4 * c0;
  if (disc < 0) return [];
  const sq = Math.sqrt(disc);
  return [(-b - sq) / 2, (-b + sq) / 2].map((t) => ({ x: line.px + line.dx * t, y: line.py + line.dy * t }));
}

function circleCircleIntersections(a: MiterBoundary & { kind: 'circle' }, b: MiterBoundary & { kind: 'circle' }): Array<{ x: number; y: number }> {
  const dx = b.cx - a.cx;
  const dy = b.cy - a.cy;
  const d = Math.hypot(dx, dy);
  if (d < 1e-9 || d > a.r + b.r || d < Math.abs(a.r - b.r)) return [];
  // Radical-line decomposition: foot at distance `along` from center A, hits
  // offset ±h perpendicular to the center line.
  const along = (a.r * a.r - b.r * b.r + d * d) / (2 * d);
  const h2 = a.r * a.r - along * along;
  const h = h2 > 0 ? Math.sqrt(h2) : 0;
  const fx = a.cx + (dx / d) * along;
  const fy = a.cy + (dy / d) * along;
  const ux = (-dy / d) * h;
  const uy = (dx / d) * h;
  return [
    { x: fx + ux, y: fy + uy },
    { x: fx - ux, y: fy - uy },
  ];
}

/**
 * Infinite-line intersection of (p1, p2) with (p3, p4). Snaps the result
 * so two adjacent computations agree.
 */
function segmentLineIntersect(p1x: number, p1y: number, p2x: number, p2y: number, p3x: number, p3y: number, p4x: number, p4y: number): { x: number; y: number } | null {
  const d1x = p2x - p1x;
  const d1y = p2y - p1y;
  const d2x = p4x - p3x;
  const d2y = p4y - p3y;
  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-9) return null;
  const dx = p3x - p1x;
  const dy = p3y - p1y;
  const t = (dx * d2y - dy * d2x) / cross;
  return snapPoint({ x: p1x + t * d1x, y: p1y + t * d1y });
}

/**
 * Max bulge (metres) a hull vertex may sit off the line through its
 * neighbours before it counts as a real corner. `snapPoint`'s 0.1 mm grid
 * can bow three collinear points (e.g. a wall's two cap corners with the
 * node between them) outward by ~0.14 mm; keeping that middle vertex makes
 * the hull edge and the wall's straight cap edge enclose a hairline sliver
 * the union then traces as a visible zero-width SPUR into the wall band.
 * Dropping bulges below this threshold shrinks the hull by <0.2 mm.
 */
const HULL_COLLINEAR_BULGE = 2e-4;

/**
 * 2D convex hull via Andrew's monotone chain algorithm. Returns vertices
 * in CCW order. Removes collinear duplicates — including NEAR-collinear
 * vertices bulging less than {@link HULL_COLLINEAR_BULGE} off the chain,
 * so snap-quantised midpoints can't produce degenerate sliver edges.
 */
function convexHull(points: { x: number; y: number }[]): { x: number; y: number }[] {
  if (points.length < 3) return points.slice();
  const pts = points.slice().sort((a, b) => (a.x - b.x) || (a.y - b.y));
  const cross = (o: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  // cross = |o→b| × (perpendicular height of `a` over o→b); popping when the
  // height is under the bulge threshold removes near-collinear midpoints.
  const popsMiddle = (o: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }) => cross(o, a, b) <= HULL_COLLINEAR_BULGE * Math.hypot(b.x - o.x, b.y - o.y);
  const lower: { x: number; y: number }[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && popsMiddle(lower[lower.length - 2], lower[lower.length - 1], p)) lower.pop();
    lower.push(p);
  }
  const upper: { x: number; y: number }[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && popsMiddle(upper[upper.length - 2], upper[upper.length - 1], p)) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * Cosine threshold for treating two walls meeting at a node as the SAME
 * straight wall passing through it. Their outgoing-from-node directions are
 * antiparallel, so the dot is near -1. -0.95 ≈ within ~18° of a straight line
 * — matches the `COLLINEAR_THRESHOLD` the offset-line junction code uses to
 * decide "this is a pass-through, not a corner".
 */
const PASS_THROUGH_DOT = -0.95;

/**
 * Max thickness difference (metres) to weld across a straight pass-through
 * junction. When two collinear walls' offset boundaries differ by less than
 * this, they're snapped to a shared thickness so the wall side renders as one
 * straight line instead of a tiny step (which also drops the extra triangle
 * that step produces). 3 cm per the wall-skinning spec.
 */
const WELD_THICKNESS_TOLERANCE = 0.03;

/**
 * Weld near-equal thicknesses across straight pass-through junctions.
 *
 * At a T-junction the "through" wall is two collinear edges sharing the node.
 * If their `leftSize`/`rightSize` differ slightly, their offset boundaries
 * land at different perpendicular distances and the wall side gets a small
 * step (one stray triangle). When the difference is under
 * {@link WELD_THICKNESS_TOLERANCE} we snap both sides to the larger value so
 * the boundary is a single straight line (walls never get thinner than
 * authored, so this can't open a gap).
 *
 * Mutates the (local) capsule copies in place — `leftSize`/`rightSize` are
 * read afterwards by `buildEdgePolygon` and `buildJunctionConnector`.
 */
function weldCollinearThicknesses(capsules: Capsule[], capsulesAtNode: Map<string, number[]>): void {
  for (const idxs of capsulesAtNode.values()) {
    if (idxs.length < 2) continue;
    for (let i = 0; i < idxs.length; i++) {
      for (let j = i + 1; j < idxs.length; j++) {
        weldPair(capsules, idxs[i], idxs[j]);
      }
    }
  }
}

/**
 * Resolve a capsule's outgoing frame at `nodePos`: the direction pointing away
 * from the node, and the left/right sizes expressed in that outgoing frame
 * (swapped when the node is the capsule's END, because reversing direction
 * flips left/right).
 */
function outgoingFrame(c: Capsule, nodePos: { x: number; y: number }): { dirX: number; dirY: number; outLeftSize: number; outRightSize: number; endAtStart: boolean } {
  const atStart = Math.abs(nodePos.x - c.ax) < 1e-7 && Math.abs(nodePos.y - c.ay) < 1e-7;
  return {
    // Endpoint TANGENTS, not chord directions — two arcs (or an arc and a
    // straight) meeting tangentially weld like collinear straights.
    dirX: atStart ? c.outStartX : c.outEndX,
    dirY: atStart ? c.outStartY : c.outEndY,
    outLeftSize: atStart ? c.leftSize : c.rightSize,
    outRightSize: atStart ? c.rightSize : c.leftSize,
    endAtStart: atStart,
  };
}

/** Write an outgoing-frame size back onto the capsule's stored left/right. */
function setOutgoingSizes(c: Capsule, endAtStart: boolean, outLeftSize: number, outRightSize: number): void {
  if (endAtStart) {
    c.leftSize = outLeftSize;
    c.rightSize = outRightSize;
  } else {
    c.leftSize = outRightSize;
    c.rightSize = outLeftSize;
  }
}

/**
 * Weld a single pair of incident capsules if they form a straight pass-through
 * and their matching offset boundaries are within tolerance.
 */
function weldPair(capsules: Capsule[], idxA: number, idxB: number): void {
  const ca = capsules[idxA];
  const cb = capsules[idxB];
  // The shared node is whichever endpoint the two capsules have in common.
  const node = sharedEndpoint(ca, cb);
  if (!node) return;

  const fa = outgoingFrame(ca, node);
  const fb = outgoingFrame(cb, node);

  // Straight pass-through: outgoing directions antiparallel.
  const dot = fa.dirX * fb.dirX + fa.dirY * fb.dirY;
  if (dot > PASS_THROUGH_DOT) return;

  // With antiparallel outgoing directions, A's outgoing-LEFT and B's
  // outgoing-RIGHT lie on the same world side (and vice versa). Weld each
  // matched pair when within tolerance, snapping to the larger size.
  let aLeft = fa.outLeftSize;
  let aRight = fa.outRightSize;
  let bLeft = fb.outLeftSize;
  let bRight = fb.outRightSize;

  if (Math.abs(aLeft - bRight) < WELD_THICKNESS_TOLERANCE) {
    const v = Math.max(aLeft, bRight);
    aLeft = v;
    bRight = v;
  }
  if (Math.abs(aRight - bLeft) < WELD_THICKNESS_TOLERANCE) {
    const v = Math.max(aRight, bLeft);
    aRight = v;
    bLeft = v;
  }

  setOutgoingSizes(ca, fa.endAtStart, aLeft, aRight);
  setOutgoingSizes(cb, fb.endAtStart, bLeft, bRight);
}

/**
 * Convex carve strip removing an arc band's poke-through past a junction
 * neighbour's FAR face.
 *
 * When a curved wall meets another wall at a shallow / near-tangential
 * angle, its band (centerline ± thickness) can sweep across the neighbour's
 * body and stick out of the neighbour's far side — a visible flap of "wall"
 * hanging beyond a wall it should terminate against. This computes the
 * convex strip just beyond the neighbour's far offset boundary (bounded to
 * the neighbour's axial extent, so nothing is carved past the neighbour's
 * real end) sized to the arc band's actual penetration. Carving it from the
 * arc's region leaves the arc flush with the neighbour's far face; overlap
 * INSIDE the neighbour's body is untouched (the union still welds them).
 *
 * The neighbour's frame is its CHORD frame — exact for straight walls,
 * an approximation for curved neighbours (their far boundary is an arc).
 * Returns null when the band doesn't poke past the far face.
 */
function computeFarSidePokeCarve(arc: Capsule, ring: THREE.Vector2[], nodePos: { x: number; y: number }, nb: Capsule): { x: number; y: number }[] | null {
  const chordX = nb.bx - nb.ax;
  const chordY = nb.by - nb.ay;
  const chordLen = Math.hypot(chordX, chordY);
  if (chordLen < 1e-9) return null;
  const dirX = chordX / chordLen;
  const dirY = chordY / chordLen;
  const perpX = -dirY;
  const perpY = dirX;

  // Which side of the neighbour's centerline does the arc's body lie on near
  // this junction? Probe the arc centerline part-way from the node — near a
  // tangential junction the first stretch hugs the centerline, so take the
  // probe with the largest perpendicular clearance.
  const aXY = { x: arc.ax, y: arc.ay };
  const bXY = { x: arc.bx, y: arc.by };
  const endAtStart = Math.abs(nodePos.x - arc.ax) < 1e-7 && Math.abs(nodePos.y - arc.ay) < 1e-7;
  let sideDist = 0;
  for (const f of [0.25, 0.5]) {
    const s = endAtStart ? f * arc.length : (1 - f) * arc.length;
    const probe = pointAtArcLength(aXY, bXY, arc.bulge, s).point;
    const d = (probe.x - nb.ax) * perpX + (probe.y - nb.ay) * perpY;
    if (Math.abs(d) > Math.abs(sideDist)) sideDist = d;
  }
  if (Math.abs(sideDist) < 1e-6) return null;

  // Far side = opposite the arc body. farX/farY is the unit normal pointing
  // beyond the far face; farOffset the face's distance from the centerline.
  const farSign = sideDist > 0 ? -1 : 1;
  const farX = perpX * farSign;
  const farY = perpY * farSign;
  const farOffset = farSign > 0 ? nb.leftSize : nb.rightSize;

  // Max penetration of the arc band's ring past the far face, within the
  // neighbour's axial extent.
  let maxPen = 0;
  for (const p of ring) {
    const rx = p.x - nb.ax;
    const ry = p.y - nb.ay;
    const axial = rx * dirX + ry * dirY;
    if (axial < 0 || axial > chordLen) continue;
    const pen = rx * farX + ry * farY - farOffset;
    if (pen > maxPen) maxPen = pen;
  }
  if (maxPen < 1e-4) return null;

  const depth = maxPen + 0.05;
  const a0 = snapPoint({ x: nb.ax + farX * farOffset, y: nb.ay + farY * farOffset });
  const a1 = snapPoint({ x: nb.bx + farX * farOffset, y: nb.by + farY * farOffset });
  const b1 = snapPoint({ x: nb.bx + farX * (farOffset + depth), y: nb.by + farY * (farOffset + depth) });
  const b0 = snapPoint({ x: nb.ax + farX * (farOffset + depth), y: nb.ay + farY * (farOffset + depth) });
  return [a0, a1, b1, b0];
}

/** Return the shared endpoint position of two capsules, or null. */
function sharedEndpoint(a: Capsule, b: Capsule): { x: number; y: number } | null {
  const eq = (x1: number, y1: number, x2: number, y2: number) => Math.abs(x1 - x2) < 1e-7 && Math.abs(y1 - y2) < 1e-7;
  if (eq(a.ax, a.ay, b.ax, b.ay) || eq(a.ax, a.ay, b.bx, b.by)) return { x: a.ax, y: a.ay };
  if (eq(a.bx, a.by, b.ax, b.ay) || eq(a.bx, a.by, b.bx, b.by)) return { x: a.bx, y: a.by };
  return null;
}

/**
 * Public entry — same signature philosophy as `computeWallRegions`. Builds
 * the SDF/bisector partition and returns `WallRegion[]`.
 */
/** Build the edge capsules + node incidence and run the pass-through
 *  thickness weld on them — the shared front half of the skin partition,
 *  also the source of the AUTHORITATIVE welded sizes (see
 *  computeWeldedEdgeSizes). */
function buildWeldedCapsules(edges: SkeletonEdgeData[], nodeMap: Map<string, SkeletonNodeData>): { capsules: Capsule[]; capsuleIdxByEdge: Map<string, number>; capsulesAtNode: Map<string, number[]> } {
  const capsules: Capsule[] = [];
  const capsuleIdxByEdge = new Map<string, number>();
  for (const e of edges) {
    const s = nodeMap.get(e.startNodeId);
    const t = nodeMap.get(e.endNodeId);
    if (!s || !t) continue;
    const c = buildCapsule(e, s, t);
    if (!c) continue;
    capsuleIdxByEdge.set(e.id, capsules.length);
    capsules.push(c);
  }
  const capsulesAtNode = new Map<string, number[]>();
  for (const e of edges) {
    const idx = capsuleIdxByEdge.get(e.id);
    if (idx === undefined) continue;
    const a = capsulesAtNode.get(e.startNodeId) ?? [];
    a.push(idx);
    capsulesAtNode.set(e.startNodeId, a);
    const b = capsulesAtNode.get(e.endNodeId) ?? [];
    b.push(idx);
    capsulesAtNode.set(e.endNodeId, b);
  }

  // Straighten near-equal-thickness pass-through junctions (T-junctions where
  // the through wall's two halves differ by <3cm) BEFORE building polygons, so
  // both the edge rectangles and the junction connector use the welded sizes.
  weldCollinearThicknesses(capsules, capsulesAtNode);
  return { capsules, capsuleIdxByEdge, capsulesAtNode };
}

/**
 * The pass-through thickness weld's AUTHORITATIVE result: edge id → welded
 * left/right sizes, for exactly the edges whose sizes the skin's weld
 * changes. This is the SAME weld `computeSdfWallRegions` applies visually —
 * callers persist the returned sizes onto the edge data so the harmonized
 * width isn't just cosmetic (post-generation and on edit commit; never
 * mid-drag). Feed WALL edges only (no `role:'OUTLINE'`).
 */
export function computeWeldedEdgeSizes(edges: SkeletonEdgeData[], nodeMap: Map<string, SkeletonNodeData>): Map<string, { leftSize: number; rightSize: number }> {
  const { capsules, capsuleIdxByEdge } = buildWeldedCapsules(edges, nodeMap);
  const welded = new Map<string, { leftSize: number; rightSize: number }>();
  for (const e of edges) {
    const idx = capsuleIdxByEdge.get(e.id);
    if (idx === undefined) continue;
    const c = capsules[idx];
    if (Math.abs(c.leftSize - (e.leftSize ?? 0.1)) > 1e-6 || Math.abs(c.rightSize - (e.rightSize ?? 0.1)) > 1e-6) {
      welded.set(e.id, { leftSize: c.leftSize, rightSize: c.rightSize });
    }
  }
  return welded;
}

/**
 * Apply {@link computeWeldedEdgeSizes} to a full edge array (outline edges
 * pass through untouched). Returns the same array reference when nothing
 * welded, else a new array with the changed edges replaced.
 */
export function applyWeldedEdgeSizes(nodes: SkeletonNodeData[], edges: SkeletonEdgeData[]): SkeletonEdgeData[] {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const welded = computeWeldedEdgeSizes(
    edges.filter((e) => e.role !== 'OUTLINE'),
    nodeMap,
  );
  if (welded.size === 0) return edges;
  return edges.map((e) => {
    const w = welded.get(e.id);
    return w ? { ...e, leftSize: w.leftSize, rightSize: w.rightSize } : e;
  });
}

export function computeSdfWallRegions(edges: SkeletonEdgeData[], nodeMap: Map<string, SkeletonNodeData>, floorHeight: number, resolveBounds: ResolveBoundsFn = defaultResolveBounds): WallRegion[] {
  const { capsules, capsuleIdxByEdge, capsulesAtNode } = buildWeldedCapsules(edges, nodeMap);

  const regions: WallRegion[] = [];
  const regionByCapsuleIdx = new Map<number, WallRegion>();
  for (const e of edges) {
    const idx = capsuleIdxByEdge.get(e.id);
    if (idx === undefined) continue;
    const poly = buildEdgePolygon(capsules, idx);
    if (!poly || poly.length < 3) continue;
    const bounds = resolveBounds(e.height, floorHeight);
    const region: WallRegion = { ring: poly, bottom: bounds.bottom, top: bounds.top };
    regionByCapsuleIdx.set(idx, region);
    regions.push(region);
  }
  // Junction-connector polygons fill the V-notch at convex corners where
  // adjacent walls' nominal rectangles don't meet. Manifold's union
  // absorbs these with the wall rectangles.
  //
  // Junctions linked by a SHORT connector edge — a jog shorter than the
  // combined thickness reach of the walls meeting at its two ends,
  // typically a room-detection artifact — form ONE physical corner, not
  // two. Rendering them as separate junctions chamfers the corner along
  // the connector's offset faces and cuts wedge notches out of it (the
  // "cut-off corners" X-ray artifact). Merge such junctions (union-find
  // across connector edges) and build a single hull whose miter apexes
  // square the corner; the connector's own band still fills the span
  // between the nodes.
  // Thickness reach of the OTHER walls meeting at `nodeId` — the candidate
  // connector itself is excluded: a jog is absorbed into the junction the
  // other walls form, so its own thickness must not vote (a long thick wall
  // would otherwise cluster its own endpoints).
  const capReachExcluding = (nodeId: string, excludeIdx: number): number => {
    let reach = 0;
    for (const i of capsulesAtNode.get(nodeId) ?? []) {
      if (i === excludeIdx) continue;
      reach = Math.max(reach, capsules[i].leftSize, capsules[i].rightSize);
    }
    return reach;
  };
  const clusterParent = new Map<string, string>();
  const findRoot = (id: string): string => {
    const parent = clusterParent.get(id);
    if (parent === undefined || parent === id) return id;
    const root = findRoot(parent);
    clusterParent.set(id, root);
    return root;
  };
  // A connector that continues STRAIGHT through an endpoint (near-antiparallel
  // to another wall there) is part of a wall RUN, not a corner jog — a short
  // straight stretch next to a very thick crossing wall must keep its own
  // junctions, or the merged hull swallows a visible piece of wall.
  const passesThroughAt = (nodeId: string, idx: number): boolean => {
    const nodeData = nodeMap.get(nodeId);
    if (!nodeData) return false;
    const nodePos = { x: nodeData.position.x, y: nodeData.position.y };
    const own = outgoingFrame(capsules[idx], nodePos);
    for (const i of capsulesAtNode.get(nodeId) ?? []) {
      if (i === idx) continue;
      const other = outgoingFrame(capsules[i], nodePos);
      if (own.dirX * other.dirX + own.dirY * other.dirY < PASS_THROUGH_DOT) return true;
    }
    return false;
  };
  for (const e of edges) {
    const idx = capsuleIdxByEdge.get(e.id);
    if (idx === undefined) continue;
    // Only merge REAL junctions (≥2 walls at both ends): a short stub wall
    // ending in open space is a dead end, not a jog inside a corner.
    if ((capsulesAtNode.get(e.startNodeId)?.length ?? 0) < 2 || (capsulesAtNode.get(e.endNodeId)?.length ?? 0) < 2) continue;
    // A jog BENDS at both ends; straight continuations stay unmerged.
    if (passesThroughAt(e.startNodeId, idx) || passesThroughAt(e.endNodeId, idx)) continue;
    if (capsules[idx].length <= capReachExcluding(e.startNodeId, idx) + capReachExcluding(e.endNodeId, idx)) {
      const ra = findRoot(e.startNodeId);
      const rb = findRoot(e.endNodeId);
      if (ra !== rb) clusterParent.set(ra, rb);
    }
  }
  const clusters = new Map<string, string[]>();
  for (const [nodeId, idxs] of capsulesAtNode) {
    if (idxs.length < 2) continue;
    const root = findRoot(nodeId);
    const list = clusters.get(root) ?? [];
    list.push(nodeId);
    clusters.set(root, list);
  }

  const connectorByNodeId = new Map<string, WallRegion>();
  for (const memberIds of clusters.values()) {
    const memberSet = new Set(memberIds);
    const members: JunctionMember[] = [];
    let bottom = Infinity;
    let top = -Infinity;
    for (const nodeId of memberIds) {
      const node = nodeMap.get(nodeId);
      if (!node) continue;
      // Walls INTERNAL to a merged junction (both endpoints collapsed into
      // it — the short connector itself) don't reach outside it; mitering
      // against them would re-introduce the chamfer the merge removes. They
      // still ride along as `internalIdxs`: their band's lateral extent
      // bounds the junction hull (see JunctionMember.internalIdxs).
      const allIdxs = capsulesAtNode.get(nodeId) ?? [];
      const isInternal = (i: number): boolean => {
        const c = capsules[i];
        const otherId = c.edge.startNodeId === nodeId ? c.edge.endNodeId : c.edge.startNodeId;
        return memberSet.size > 1 && memberSet.has(otherId);
      };
      const incidentIdxs = allIdxs.filter((i) => !isInternal(i));
      if (incidentIdxs.length === 0) continue;
      members.push({ nodePos: { x: node.position.x, y: node.position.y }, incidentIdxs, internalIdxs: allIdxs.filter(isInternal) });
      // A junction without its own height constraint spans the walls meeting
      // at it. Resolving the missing constraint as the default (ceiling) would
      // leave a full-height pillar poking out of walls whose top was lowered.
      const boundsList = node.height ? [resolveBounds(node.height, floorHeight)] : allIdxs.map((i) => resolveBounds(capsules[i].edge.height, floorHeight));
      for (const bounds of boundsList) {
        bottom = Math.min(bottom, bounds.bottom);
        top = Math.max(top, bounds.top);
      }
    }
    if (members.length === 0 || !Number.isFinite(bottom)) continue;
    const connector = buildJunctionConnector(capsules, members);
    if (!connector) continue;
    const region: WallRegion = { ring: connector, bottom, top, isNodeCell: true };
    for (const nodeId of memberIds) connectorByNodeId.set(nodeId, region);
    regions.push(region);
  }
  // Carve arc poke-throughs: a curved wall meeting a neighbour near-
  // tangentially can sweep its band across the neighbour and out its far
  // side (an overgrown arch punching through an adjacent wall). Carve the
  // overshoot strip from the arc's region — and from the node's junction
  // connector, whose cap corners follow the arc's — so the arch terminates
  // flush with the neighbour's far face. See `computeFarSidePokeCarve`.
  for (const [nodeId, idxs] of capsulesAtNode) {
    if (idxs.length < 2) continue;
    const node = nodeMap.get(nodeId);
    if (!node) continue;
    const nodePos = { x: node.position.x, y: node.position.y };
    for (const i of idxs) {
      if (capsules[i].bulge === 0) continue;
      const region = regionByCapsuleIdx.get(i);
      if (!region) continue;
      for (const j of idxs) {
        if (j === i) continue;
        const carve = computeFarSidePokeCarve(capsules[i], region.ring, nodePos, capsules[j]);
        if (!carve) continue;
        (region.subtract ??= []).push(carve.map((p) => new THREE.Vector2(p.x, p.y)));
        const connector = connectorByNodeId.get(nodeId);
        if (connector) (connector.subtract ??= []).push(carve.map((p) => new THREE.Vector2(p.x, p.y)));
      }
    }
  }
  return regions;
}
